import { Box, Divider, Typography } from '@material-ui/core';
import type {
  ControlPlaneCapabilities,
  ControlPlaneCapability,
  PairingPurpose,
} from '@internal/spassu-code-contract';
import type { ControlPlaneOnboardingView } from '../hooks/useControlPlaneOnboarding';
import {
  DENIED_CAPABILITY,
  type ControlPlaneManagementView,
} from '../hooks/useControlPlaneManagement';
import { CapabilityButton } from './ControlPlaneCapabilityButton';
import { SessionActions, canRevoke } from './ControlPlaneSessionActions';
import { useConnectionPageStyles } from './useConnectionPageStyles';

export interface ManagementActionProps {
  management: ControlPlaneManagementView;
  onboarding: ControlPlaneOnboardingView;
  activityTitle: string;
  openCockpit(): void;
}

interface ActionGroupProps extends ManagementActionProps {
  busy: boolean;
  onPair(purpose: PairingPurpose): void;
  onResume(): void;
  onCancel(): void;
}

const isAllowed = (capability?: ControlPlaneCapability) =>
  Boolean(capability?.allowed);

function hasAny(
  capabilities: ControlPlaneCapabilities | undefined,
  keys: (keyof ControlPlaneCapabilities)[],
): boolean {
  return keys.some(key => isAllowed(capabilities?.[key]));
}

export function PrimaryConnectionActions(props: ActionGroupProps) {
  const classes = useConnectionPageStyles();
  const capabilities = props.management.projection?.capabilities;
  const visible =
    hasAny(capabilities, ['openCockpit', 'renew', 'recover', 'resume']) ||
    canRevoke(props.onboarding, props.management.projection);
  if (!visible) return null;
  return (
    <Box className={classes.row}>
      <CapabilityButton
        capability={capabilities?.openCockpit ?? DENIED_CAPABILITY}
        label="Abrir Cockpit"
        busy={false}
        variant="contained"
        onClick={props.openCockpit}
      />
      <CapabilityButton
        capability={capabilities?.renew ?? DENIED_CAPABILITY}
        label="Renovar conexão"
        busy={props.busy}
        onClick={() => props.onPair('renewal')}
      />
      <CapabilityButton
        capability={capabilities?.recover ?? DENIED_CAPABILITY}
        label="Recuperar conexão"
        busy={props.busy}
        onClick={() => props.onPair('recovery')}
      />
      <CapabilityButton
        capability={capabilities?.resume ?? DENIED_CAPABILITY}
        label="Retomar conexão"
        busy={props.busy}
        onClick={props.onResume}
      />
      <SessionActions
        view={props.onboarding}
        projection={props.management.projection}
      />
    </Box>
  );
}

export function TerminalActivityActions(props: ActionGroupProps) {
  const classes = useConnectionPageStyles();
  const capabilities = props.management.projection?.capabilities;
  if (!hasAny(capabilities, ['finalize', 'cancel'])) return null;
  return (
    <>
      <Divider />
      <Box className={classes.group}>
        <Typography variant="overline" component="h3">
          Encerrar atividade
        </Typography>
        <Box className={classes.row}>
          <CapabilityButton
            capability={capabilities?.finalize ?? DENIED_CAPABILITY}
            label="Finalizar atividade"
            busy={props.busy}
            onClick={() => {
              void props.management.finalize();
            }}
          />
          <CapabilityButton
            capability={capabilities?.cancel ?? DENIED_CAPABILITY}
            label="Cancelar atividade"
            busy={props.busy}
            color="secondary"
            onClick={props.onCancel}
          />
        </Box>
      </Box>
    </>
  );
}

export function WorkspaceCleanupActions(props: ActionGroupProps) {
  const classes = useConnectionPageStyles();
  const capability = props.management.projection?.capabilities.enrollCleanup;
  if (!isAllowed(capability)) return null;
  return (
    <Box className={classes.group}>
      {/* Only the canonical capability grants cleanup enrollment; a terminal
          activity never regains operational access through this action. */}
      <Typography variant="body2">
        O workspace local ainda não foi removido. Atualize o agente para
        concluir a exclusão.
      </Typography>
      <Box className={classes.row}>
        <CapabilityButton
          capability={capability ?? DENIED_CAPABILITY}
          label="Atualizar agente para concluir a exclusão"
          busy={props.busy}
          onClick={() => props.onPair('cleanup-enrollment')}
        />
      </Box>
    </Box>
  );
}
